// Password recovery detection and validation logic

/**
 * Detects if the current auth event is a password recovery event
 * @param {string} eventName - The auth state change event name (e.g., 'PASSWORD_RECOVERY', 'SIGNED_IN')
 * @returns {boolean}
 */
export function isRecoveryEvent(eventName) {
  return eventName === 'PASSWORD_RECOVERY'
}

/**
 * Detects if the URL hash contains recovery session parameters
 * @param {string} hash - window.location.hash
 * @returns {boolean}
 */
export function isRecoveryHash(hash) {
  if (!hash) return false
  // Hash format: #type=recovery&access_token=...
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  return params.get('type') === 'recovery'
}

/**
 * Validates password meets requirements
 * @param {string} password
 * @returns {{ valid: boolean, error?: string }}
 */
export function validatePassword(password) {
  if (!password || password.length < 6) {
    return { valid: false, error: 'Password must be at least 6 characters' }
  }
  return { valid: true }
}

/**
 * Validates password and confirm match
 * @param {string} password
 * @param {string} confirm
 * @returns {{ valid: boolean, error?: string }}
 */
export function validatePasswordMatch(password, confirm) {
  const passwordValidation = validatePassword(password)
  if (!passwordValidation.valid) return passwordValidation

  if (password !== confirm) {
    return { valid: false, error: 'Passwords do not match' }
  }

  return { valid: true }
}

/**
 * Determines if a protected route should redirect to reset-password
 * @param {boolean} recoveryPending - Whether a recovery session is active
 * @param {string} pathname - Current route pathname
 * @returns {boolean}
 */
export function shouldRedirectToReset(recoveryPending, pathname) {
  if (!recoveryPending) return false

  // Don't redirect if already on reset-password
  if (pathname === '/reset-password') return false

  // Public routes that don't require redirect
  const publicRoutes = ['/login', '/health', '/image-removal', '/privacy', '/terms', '/support', '/explore']
  if (publicRoutes.includes(pathname)) return false
  if (pathname.startsWith('/shared/')) return false

  // All other routes should redirect during recovery
  return true
}
